import React from 'react'
import { Menu, Download, Share } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { UserMenuContent } from './UserMenuContent'
import { useCollaborations } from '@/hooks/useCollaborations'
import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useIsMobile } from '@/hooks/use-mobile'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { NotificationBell } from './NotificationBell'
import { athleteNavigationItems } from './athleteNavigation'
import { useInstallApp } from '@/hooks/useInstallApp'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
const Header = () => {
  const { bids } = useCollaborations()
  const { athlete, logout } = useAuth()
  const hasNewBids = bids.length > 0
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()
  const { canInstall, showInstall, promptInstall } = useInstallApp()
  const [iosInstallOpen, setIosInstallOpen] = useState(false)

  const handleInstallClick = async () => {
    if (canInstall) {
      setMobileMenuOpen(false)
      await promptInstall()
    } else {
      setIosInstallOpen(true)
    }
  }

  const navigationItems = athleteNavigationItems
  const handleNavItemClick = (path) => {
    navigate(path)
    setMobileMenuOpen(false)
  }
  return (
    <header className="sticky top-0 z-20 w-full bg-[#10181D] border-b border-white/10 px-4 py-2 md:px-6 md:py-3 flex justify-between items-center">
      <Link to="/" className="flex items-center gap-2">
        <img
          src="https://adthlete.us-iad-1.linodeobjects.com/public/athios-logo-white.png"
          alt="Athios Logo"
          className="h-6 md:h-7"
        />
      </Link>

      {/* Notification bell - shown on both mobile and desktop */}
      <div className="ml-auto">
        <NotificationBell />
      </div>

      {/* Mobile: Profile button with dropdown - with settings and logout options */}
      {isMobile && (
        <div className="flex items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger className="focus:outline-hidden">
              <Avatar className="h-8 w-8 border border-white/20">
                <AvatarImage src={athlete?.avatar} alt={athlete?.name} />
                <AvatarFallback className="bg-white/10 text-white text-xs">
                  {athlete?.name?.charAt(0) || 'A'}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-[#1A2530] border border-white/10 text-white"
            >
              <UserMenuContent showUserInfo={true} />
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Menu button - placed right after profile */}
          <button
            className="md:hidden p-1.5"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <Menu className="w-6 h-6 text-gray-300" />
          </button>
        </div>
      )}

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-30 bg-[#10181D] md:hidden flex flex-col">
          <div className="flex justify-between items-center p-4 border-b border-white/10">
            <Link
              to="/"
              className="flex items-center gap-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <img
                src="https://adthlete.us-iad-1.linodeobjects.com/public/athios-logo-white.png"
                alt="Athios Logo"
                className="h-6"
              />
            </Link>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-full hover:bg-white/10 text-white"
              aria-label="Close menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <nav className="flex-1 overflow-auto p-4">
            <ul className="space-y-1">
              {navigationItems.map((item) => {
                const isActive = item.end
                  ? location.pathname === item.path
                  : location.pathname.startsWith(item.path)
                const Icon = item.icon
                return (
                  <li key={item.path}>
                    <button
                      onClick={() => handleNavItemClick(item.path)}
                      className={`w-full text-left py-3 px-3 rounded-md flex items-center ${isActive ? 'bg-[#C0FD72] text-black' : 'text-gray-300 hover:bg-white/5 hover:text-white'}`}
                    >
                      <Icon
                        className="w-6 h-6 mr-3 shrink-0"
                        style={{ color: isActive ? 'black' : item.color }}
                      />
                      <span>{item.label}</span>
                    </button>
                  </li>
                )
              })}
              {showInstall && (
                <li>
                  <button
                    onClick={handleInstallClick}
                    className="w-full text-left py-3 px-3 rounded-md flex items-center text-gray-300 hover:bg-white/5 hover:text-white"
                  >
                    <Download className="w-6 h-6 mr-3 shrink-0" style={{ color: '#C0FD72' }} />
                    <span>Install app</span>
                  </button>
                </li>
              )}
            </ul>
          </nav>
        </div>
      )}

      <Dialog open={iosInstallOpen} onOpenChange={setIosInstallOpen}>
        <DialogContent className="bg-[#1A2530] border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Install Athios</DialogTitle>
            <DialogDescription className="text-gray-300">
              Add Athios to your Home Screen to open it like an app and turn on notifications.
            </DialogDescription>
          </DialogHeader>
          <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-200">
            <li>
              Open this page in <strong>Safari</strong>.
            </li>
            <li>
              Tap the <Share className="inline h-4 w-4 align-text-bottom" /> Share button.
            </li>
            <li>
              Choose <strong>Add to Home Screen</strong>, then tap <strong>Add</strong>.
            </li>
          </ol>
        </DialogContent>
      </Dialog>
    </header>
  )
}
export default Header
